/* eslint-disable ember/no-computed-properties-in-native-classes */
import Service from '@ember/service';
import { service } from '@ember/service';
import { computed } from '@ember/object';
import { isEmpty } from '@ember/utils';
import AuTaxType from '@linc-technologies/linced-common/enums/au-tax-type';
import CountryCode from '@linc-technologies/linced-common/enums/country-code';
import { byKeys, uniqBy } from '@linc-technologies/linced-common/utils/arrays';
import moment from 'moment';

const TAX_RATES = {
  [CountryCode.AU]: [
    {
      effectiveDate: '2000-07-01',
      rate: 1000,
    },
  ],
  [CountryCode.NZ]: [
    {
      effectiveDate: '1986-10-01',
      rate: 1000,
    },
    {
      effectiveDate: '1989-07-01',
      rate: 1250,
    },
    {
      effectiveDate: '2010-10-01',
      rate: 1500,
    },
  ],
};

const TAX_YEAR_START_DATES = {
  [CountryCode.AU]: '07-01',
  [CountryCode.NZ]: '04-01',
};

export default class TaxService extends Service {
  @service
  session;

  @computed('session.school.countryCode')
  get taxLabel() {
    let countryCode = this.session.get('school.countryCode') || CountryCode.NZ;
    return this.getTaxLabelForCountry(countryCode);
  }

  getTaxLabelForCountry(countryCode) {
    switch (countryCode) {
      case CountryCode.AU:
      case CountryCode.NZ:
        return 'GST';
      case CountryCode.GB:
        return 'VAT';
      default:
        return 'Tax';
    }
  }

  getRate(date, taxCode) {
    // Default country code to NZ for now.
    let countryCode = this.session.get('school.countryCode') || CountryCode.NZ;
    return this.getRateForCountry(date, countryCode, taxCode);
  }

  getRateForCountry(date, countryCode, taxCode) {
    if (isEmpty(date)) {
      date = moment().format('YYYY-MM-DD');
    }

    if (countryCode === CountryCode.AU && taxCode !== AuTaxType.S1) {
      // Only tax code S1 attracts GST in Australia.
      return 0;
    }

    if (!isEmpty(TAX_RATES[countryCode])) {
      let rates = TAX_RATES[countryCode];
      let dateMoment = moment(date);
      let effectiveRates = rates
        .filter((rate) => {
          return moment(rate.effectiveDate).isBefore(dateMoment);
        })
        .sort(byKeys('effectiveDate'));
      return effectiveRates.at(-1)?.rate ?? 0;
    }

    // TODO: Determine a better way to handle unknown tax jurisdictions.
    return false;
  }

  getTaxYears(startDate, endDate) {
    // Default country code to NZ for now.
    let countryCode = this.session.get('school.countryCode') || CountryCode.NZ;

    return this.getTaxYearsForCountry(startDate, endDate, countryCode);
  }

  getTaxYearsForCountry(startDate, endDate, countryCode) {
    let years = [];

    if (!isEmpty(TAX_YEAR_START_DATES[countryCode])) {
      let taxYearStart = TAX_YEAR_START_DATES[countryCode];
      let start = moment(startDate, 'YYYY-MM-DD');
      let end = moment(endDate, 'YYYY-MM-DD');
      let startYear = start.year();
      let endYear = end.year();

      if (taxYearStart === '01-01') {
        // Tax year matches calendar year - too easy!
        let year = startYear;
        while (year <= endYear) {
          years.push({
            label: year,
            start: `${year}-01-01`,
            end: `${year}-12-31`,
          });
          year++;
        }
      } else {
        // Tax year doesn't match calendar year.
        let taxStart = moment(`${startYear}-${taxYearStart}`, 'YYYY-MM-DD');
        let check = start;
        let endChecked = false;
        while (check.year() <= endYear) {
          if (check.isAfter(end)) {
            check = end;
            endChecked = true;
          }

          let year = check.year();

          if (check.isBefore(taxStart, 'day')) {
            years.push({
              label: `${year - 1}/${year}`,
              start: moment(taxStart).subtract(1, 'year').format('YYYY-MM-DD'),
              end: moment(taxStart).subtract(1, 'day').format('YYYY-MM-DD'),
            });
          } else {
            years.push({
              label: `${year}/${year + 1}`,
              start: taxStart.format('YYYY-MM-DD'),
              end: moment(taxStart)
                .add(1, 'year')
                .subtract(1, 'day')
                .format('YYYY-MM-DD'),
            });
          }

          taxStart.add(1, 'year');
          check.add(1, 'year');
        }

        if (!endChecked) {
          // Push the year back so that we can check the end date.
          taxStart.subtract(1, 'year');
          check.subtract(1, 'year');

          if (check.isBefore(end) && end.isSameOrAfter(taxStart, 'day')) {
            // End date is in a different tax year to the offset start date.
            let year = end.year();
            years.push({
              label: `${year}/${year + 1}`,
              start: taxStart.format('YYYY-MM-DD'),
              end: moment(taxStart)
                .add(1, 'year')
                .subtract(1, 'day')
                .format('YYYY-MM-DD'),
            });
          }
        }

        years = uniqBy(years, 'label');
      }
    }

    return years;
  }
}
